import * as params from '../../util/getQueryParams';
import { SearchBoosterResults } from './SearchBoosterResults';
import { dates } from '../../util/time';
import { createRoot } from 'react-dom/client';
import React, { ChangeEvent } from 'react';
import { events } from '../../util/Events';
import { FilterControls } from './SearchBoosterFilterControls';
import { SearchBoosterResultsSummary } from './SearchBoosterResultsSummary';
import uniqBy from 'lodash/uniqBy';
import orderBy from 'lodash/orderBy';
import filter from 'lodash/filter';
import { SearchBoosterValidationError } from './SearchBoosterValidationError';
import { ajaxLink, ajaxNonce } from '../../App';
import jqXHR = JQuery.jqXHR;
import { TrainingSearchResult } from '../../models/Examentraining';
import { addWeeks, isSameWeek, parseISO, startOfWeek } from 'date-fns';

export enum SortType {
  DATE = 'datum_start',
  DISTANCE = 'distance',
}
export interface FilterOptions {
  filter: {
    month?: string;
    type?: string;
    distance?: number;
  };
  sort: SortType;
  acfData: {
    start_date: string;
    end_date: string;
  };
}


/**
 * This component performs a search based on the values passed query parameters
 */
export class SearchBooster {

  /**
   * Determine whether we are on the examntraining or codelab page.
   * (this value is set in twig)
   */
  private product: 'examenbooster';

  public globalResults: TrainingSearchResult[] = [];

  /**
   * The list of query parameters containing the search filters.
   */
  private queryParams: params.QueryParams = {};

  /**
   * The active frontend filters.
   */
  public filterOptions: FilterOptions;

  /**
   * Flag to determine if we should display the "not found error"
   */
  private locationNotFound: boolean;

  constructor() {
    /**
     * The date range is set by advanced custom fields on some pages like:
     * https://www.lyceo.nl/examentraining/herexamentraining/
     */
    const acfData = $('#sticky-search').data('acf');
    this.product = $('[data-container="search-results"]').data('product');
    this.queryParams = params.getQueryParams(document.location.search);

    /**
     * In the period from March till May there are about 2500 trainings
     * available. This means we send at least 4mb of data to the frontend,
     * when a user doesn't define any filters like subject, level or city.
     * To reduce this size, we force the user to select a level first.
     */
    if (!this.queryParams.level && this.product === 'examenbooster') {
      const error: string = 'Selecteer eerst een niveau om de beschikbare examentrainingen weer te geven.';
      this.renderNoResultsError(error);

      // open the filter dropdown on mobile view
      $('.front-search__form, .front-search__errors').toggleClass('open');
      return;
    }

    // set default filter options
    this.filterOptions = {
      filter: {
        month: '',
        type: '',
        distance: 500,
      },
      // Check if the sort parameter is set, otherwise fall back
      // to default sorting based on distance
      sort: this.queryParams.sort as string === SortType.DATE
        ? SortType.DATE
        : SortType.DISTANCE,
      // Check if the start and end date is set using acf
      // sometimes used for herexamens period.
      acfData: acfData || { start_date: '', end_date: '' },
    };

    // bind events
    events.on('SearchFilterChanged', (e: ChangeEvent<HTMLSelectElement>) => this.changeFilter(e));

    this.fetchResults();
  }

  public fetchResults(): void {
    const pageData = $('[data-container="search-results"]').data('data');
    const method = 'getBoostLocations';

    $.ajax({
      url: ajaxLink,
      method: 'POST',
      data: {
        action: 'lyceo',
        method: method,
        nonce: ajaxNonce,
        city: (pageData && pageData.city) ? pageData.city : '',
        controller: 'TrainingSearchResults',
        ...this.queryParams,
      },
      success: /* istanbul ignore next */ data => this.handleResponse(data),
      error: /* istanbul ignore next */ () => this.renderNoResultsError(
        'Er is iets misgegaan bij het ophalen van de examentrainingen'
      ),
    });
  }

  public handleResponse(data: {results: TrainingSearchResult[]; search_data: {point_zero: number|string}}): void {
    /** Add dateGroup property to results */
    const results = this.addDateGroupProperty(data.results);
    this.globalResults = results;
    this.locationNotFound = data.search_data.point_zero === 0;

    this.renderResults();
  }

  /**
   * Update the filterOptions depending on the value of
   * the changed select box in <SearchFilterControls/>
   * @param e
   */
  public changeFilter(e: any): void {
    let { name, value } = e.target;
    switch (name) {
      case 'type':
        this.filterOptions.filter.type = value;
        break;
      case 'month':
        this.filterOptions.filter.month = value;
        break;
      case 'distance':
        this.filterOptions.filter.distance = parseInt(value, 10);
        break;
      case 'sort':
        this.filterOptions.filter.month = '';
        this.filterOptions.sort = (value === SortType.DISTANCE)
          ? SortType.DISTANCE
          : SortType.DATE;
        break;
    }
    // collapse mobile filter options
    $('.front-search__form--ext').removeClass('open');
    this.renderResults();
  }

  /**
   * Add dateGroup property to results to be able to
   * sort by by this property
   */
  public addDateGroupProperty(results: TrainingSearchResult[]): TrainingSearchResult[] {
    return results.map(result => {
      let startDate: Date = parseISO(result.datum_start);

      const thisWeekMonday: Date = startOfWeek(new Date(), { weekStartsOn: 1 });
      const nextWeekMonday: Date = addWeeks(thisWeekMonday, 1);

      const isThisWeek: boolean = isSameWeek(new Date(), startDate, { weekStartsOn: 1 });
      const isNextWeek: boolean = isSameWeek(nextWeekMonday, startDate, { weekStartsOn: 1 });

      if (isThisWeek) {
        result.dateGroup = 'Deze week';
      } else if (isNextWeek) {
        result.dateGroup = 'Volgende week';
      } else {
        result.dateGroup = 'Later';
      }

      // Cast the distance to a number so we can sort it in filterResults().
      result.distance = Number(result.distance);

      return result;
    });
  }

  /**
   * Filter results based on filterOptions
   * @returns {Array}
   */

  /* istanbul ignore next */
  public filterResults(): TrainingSearchResult[] {
    const { start_date, end_date } = this.filterOptions.acfData;

    /** sort by distance or datum_start */
    let results = orderBy(this.globalResults, this.filterOptions.sort);

    /* istanbul ignore next */
    return filter(results, (result: TrainingSearchResult) => {
      /**  Remove trainings before today */
      let now = new Date();
      let startDate = new Date(result.datum_start);
      if (dates.compare(startDate, now) === -1) {
        return false;
      }

      /** Remove trainings with undesired month of distances (set by user) */
      let distance = result.distance < this.filterOptions.filter.distance;
      let typeFilter = result.product_naam === this.filterOptions.filter.type
        || this.filterOptions.filter.type === '';
      let month = result.datum_start.substr(0, 7) === this.filterOptions.filter.month;
      const datum_start = new Date(result.datum_start);
      const datum_eind = new Date(result.datum_eind);

      if (start_date !== '' && end_date !== '') { // Filter on period
        const filterFrom = new Date(start_date).getTime();
        const filterTo = new Date(end_date).getTime();
        const startStamp = new Date(datum_start).getTime();
        const endStamp = new Date(datum_eind).getTime();

        // Compare time stamps
        let startDateIsOk = startStamp <= filterTo && startStamp >= filterFrom;
        let endDateIsOk = endStamp <= filterTo && endStamp >= filterFrom;

        // Return when either start date or end date is within range:
        return startDateIsOk || endDateIsOk;
      } else if (this.filterOptions.filter.month !== '') {
        return month && distance && typeFilter;
      }
      return distance && typeFilter;

    });
  }

  /**
   * Notify the user to
   */
  public renderNoResultsError(error: string = 'Er is iets misgegaan'): void {
    $('.js-search-errors').addClass('visible');
    const errorContainer = $('[data-container="search-validation-error"]');
    errorContainer.show();
    const root = createRoot(errorContainer[0]);
    root.render(
        <>
          <SearchBoosterValidationError error={error}/>
        </>
    );
    $('[data-container="search-results"]').hide();
  }

  /**
   * Filter the results based on the set filterOptions
   * Group the search results into groups of items
   * Render the React Components
   */
  public renderResults(): void {
    const trainingTypes: string[] = [...new Set(this.globalResults.map(item => item.product_naam))] as string[];
    /** Filter results based on filterOptions */
    let results: TrainingSearchResult[] = (this.globalResults.length > 0) ? this.filterResults() : [];

    /** Create groups of vestigingen / weeks */
    const sortType = this.filterOptions.sort;
    /* istanbul ignore next */
    let resultGroups = uniqBy(results,
      (result: TrainingSearchResult) => (sortType === SortType.DISTANCE ? result.loc_naam : result.dateGroup));

    /**
     * Render all the search result components
     */
    const root = createRoot($('[data-container="search-results"]')[0]);
    root.render(
        <>
          <SearchBoosterResults resultGroups={resultGroups}
                                filterOptions={this.filterOptions}
                                results={results}
                                locationNotFound={this.locationNotFound}
                                types={trainingTypes}
                                queryParams={this.queryParams}/>
        </>
    );

    /**
     * Render a second copy of the summary component in the FrontSearchForm
     * component, these filters are used in the dropdown of the mobile view
     * The other copy is for desktop and is rendered in <SearchResults/>
     */
    const root2 = createRoot($('[data-container="search-results-summary"]')[0]);
    root2.render(
      <SearchBoosterResultsSummary
        city={this.queryParams.city as string}
        locationNotFound={this.locationNotFound}
        numTrainings={results.length}
        numLocations={resultGroups.length}
        type={this.filterOptions.sort}/>
    );

    /**
     * Render a second copy of the filter component in the FrontSearchForm
     * component, these filters are used in the dropdown of the mobile view
     * The other copy is for desktop and is rendered in <SearchResults/>
     */
    const root3 = createRoot($('[data-container="search-results-filter"]')[0]);
    root3.render(
        <>
          <FilterControls
              filterOptions={this.filterOptions}
              types={trainingTypes}/>
        </>
    );
  }
}
